import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  useTheme,
  alpha,
  Card,
  IconButton,
  keyframes,
  Alert,
  Button,
  Snackbar,
  CircularProgress,
  Skeleton,
  Paper,
  Divider,
  Tooltip,
  Dialog,
  DialogContent,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { backend_api } from "../../../static/server";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAIActivation } from "../../AiBotUtils/AIActivationContext";
import {
  clearPendingAISubscriptionIntent,
  persistPendingAISubscriptionIntent,
} from "../../AiBotUtils/aiSubscriptionIntent";
import {
  AccessTimeRounded,
  AnalyticsRounded,
  ArrowForwardRounded,
  AttachMoneyRounded,
  AutoAwesomeRounded,
  CampaignRounded,
  CheckCircleRounded,
  CloseRounded,
  DescriptionRounded,
  PeopleRounded,
  PlayCircleOutline,
  PowerSettingsNewRounded,
  ReceiptLongRounded,
  RocketLaunchRounded,
  SecurityRounded,
  SettingsRounded,
  ShieldRounded,
  StopCircleOutlined,
  TrendingUpRounded,
} from "@mui/icons-material";

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

// Static AI Employees Data
const staticAIEmployees = [
  {
    id: "markai",
    name: "Markai",
    title: "AI CMO",
    icon: <CampaignRounded />,
    description: "Digital marketing & brand strategy",
    detailedDescription:
      "Automates marketing operations including SEO, content strategy, lead generation, and brand development. Analyzes market trends and optimizes your firm's online presence for maximum visibility.",
    features: [
      "SEO Automation",
      "Lead Tracking",
      "Content Strategy",
      "Brand Analytics",
    ],
  },
  {
    id: "fincai",
    name: "Fincai",
    title: "AI CFO",
    icon: <AttachMoneyRounded />,
    description: "Financial management & profitability",
    detailedDescription:
      "Provides real-time financial insights, automates billing, tracks expenses, and optimizes pricing strategies to maximize profitability while maintaining healthy cash flow.",
    features: ["Profit Analysis", "Cash Flow", "Pricing", "Tax Compliance"],
  },
  {
    id: "operai",
    name: "Operai",
    title: "AI COO",
    icon: <SettingsRounded />,
    description: "Workflow automation & optimization",
    detailedDescription:
      "Designs and implements SOPs, automates routine tasks, identifies bottlenecks, and creates efficient workflows that reduce operational costs by 30%.",
    features: [
      "Process Automation",
      "Workflow Design",
      "SOP Management",
      "Performance Analytics",
    ],
  },
  {
    id: "parrai",
    name: "Parrai",
    title: "AI Paralegal",
    icon: <DescriptionRounded />,
    description: "Legal process management",
    detailedDescription:
      "Manages all paralegal functions including case tracking, deadline management, document preparation, and compliance checks with 99.9% accuracy.",
    features: [
      "Deadline Management",
      "Document Templates",
      "Case Tracking",
      "Compliance Checks",
    ],
  },
  {
    id: "salesai",
    name: "Salesai",
    title: "AI Sales Head",
    icon: <TrendingUpRounded />,
    description: "Lead conversion & revenue growth",
    detailedDescription:
      "Automates the entire sales funnel from lead qualification to conversion, using AI-powered insights to increase close rates by 35%.",
    features: [
      "Lead Qualification",
      "Sales Automation",
      "Conversion Tracking",
      "Performance Analytics",
    ],
  },
  {
    id: "hrrai",
    name: "Hrrai",
    title: "AI HR Head",
    icon: <PeopleRounded />,
    description: "Talent management & analytics",
    detailedDescription:
      "Manages hiring processes, performance reviews, team development, and retention strategies using data-driven insights for optimal team composition.",
    features: [
      "Talent Acquisition",
      "Performance Management",
      "Team Analytics",
      "Retention Strategies",
    ],
  },
  {
    id: "complai",
    name: "Complai",
    title: "AI Compliance",
    icon: <ShieldRounded />,
    description: "Risk & compliance automation",
    detailedDescription:
      "Monitors regulatory changes, manages compliance requirements, and identifies potential risks before they become issues, reducing compliance costs by 40%.",
    features: [
      "Risk Assessment",
      "Compliance Monitoring",
      "Audit Management",
      "Ethics Tracking",
    ],
  },
  {
    id: "docai",
    name: "Docai",
    title: "AI Document Manager",
    icon: <ReceiptLongRounded />,
    description: "Document generation & management",
    detailedDescription:
      "Automatically generates, reviews, and manages legal documents with AI-powered accuracy, reducing document preparation time by 80%.",
    features: [
      "Document Generation",
      "Template Management",
      "Automated Review",
      "Version Control",
    ],
  },
];

const normalizeName = (name) => name.toLowerCase().replace(/\s+/g, "");

const AIEmployeePage = () => {
  const { token } = useSelector((state) => state.user);
  const theme = useTheme();
  const {
    activatedAIs,
    activateAI,
    toggleAI,
    isAIActivated,
    loading: activationLoading,
  } = useAIActivation();

  const [selectedAI, setSelectedAI] = useState(null);
  const [cardPosition, setCardPosition] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [aiEmployees, setAiEmployees] = useState([]);
  const [ownedSubscriptions, setOwnedSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [activationInProgress, setActivationInProgress] = useState(null);
  const [foundingPopup, setFoundingPopup] = useState({ open: false, html: "" });

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backend_api}ai-employees/catalog/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      const catalog = res.data.results || [];
      const merged = staticAIEmployees.map((staticEmp) => {
        const staticNameNorm = normalizeName(staticEmp.name);
        const backendMatch = catalog.find(
          (item) => normalizeName(item.name) === staticNameNorm,
        );
        return {
          ...staticEmp,
          ...backendMatch,
          icon: staticEmp.icon,
          description: staticEmp.description,
          detailedDescription: staticEmp.detailedDescription,
          features: staticEmp.features,
        };
      });
      setAiEmployees(merged);
    } catch (err) {
      console.error("Error fetching catalog:", err);
      toast.info("Failed to load AI employees catalog");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchOwned = useCallback(async () => {
    try {
      const res = await axios.get(
        `${backend_api}ai-employees/my-subscriptions/`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const subscriptions = res.data.results || [];
      setOwnedSubscriptions(subscriptions);
    } catch (err) {
      console.error("Error fetching owned subscriptions:", err);
      toast.error("Failed to load your subscriptions");
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    fetchCatalog();
    fetchOwned();
  }, [token, fetchCatalog, fetchOwned]);

  const handleSubscribe = async (botCode) => {
    setSubscribing(true);
    try {
      await axios.post(
        `${backend_api}ai-employees/direct-subscribe/`,
        {
          bot_code: botCode,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const subscribedEmployee =
        aiEmployees.find((employee) => employee.code === botCode) ||
        (selectedAI?.code === botCode ? selectedAI : null);

      clearPendingAISubscriptionIntent();
      activateAI(subscribedEmployee?.name || botCode);
      toast.success("Successfully subscribed! AI activated automatically.");
      await fetchOwned();
      setTimeout(() => handleClosePopup(), 1500);
    } catch (err) {
      console.error("Subscription error:", err);
      // Check for founding‑50 error
      if (
        err.response?.status === 400 &&
        err.response?.data?.error_subscription
      ) {
        setFoundingPopup({
          open: true,
          html: err.response.data.error_subscription,
        });
      } else {
        const errorMsg =
          err.response?.data?.message ||
          "Subscription failed. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleFoundingAccessRedirect = useCallback(() => {
    persistPendingAISubscriptionIntent({
      botCode: selectedAI?.code,
      aiName: selectedAI?.name,
      sourcePath: "/dashboard/ai",
    });
    setFoundingPopup({ open: false, html: "" });
    window.location.assign("/founding-50");
  }, [selectedAI]);

  const handleActivationToggle = (employee, event) => {
    event?.stopPropagation();
    const { isOwned } = getEmployeeOwnershipDetails(employee);
    if (!isOwned) {
      showToast("Please subscribe to this AI employee first.", "warning");
      return;
    }

    const willDeactivate = isAIActivated(employee.name);
    setActivationInProgress(employee.name);
    setTimeout(() => {
      toggleAI(employee.name);
      showToast(
        `${employee.name} ${willDeactivate ? "deactivated" : "activated"} successfully!`,
        "success",
      );
      setActivationInProgress(null);
    }, 500);
  };

  const handleCardClick = (employee, event) => {
    // Don't open popup if clicking on activation switch
    if (event.target.closest(".activation-switch")) {
      return;
    }
    const mainContainer = document.getElementById("main-scroll-container");
    if (mainContainer) {
      const containerRect = mainContainer.getBoundingClientRect();
      setCardPosition({
        x: containerRect.left + containerRect.width / 2 - 150,
        y: containerRect.top + containerRect.height / 2 - 200,
        width: 300,
        height: 400,
      });
    }
    setSelectedAI(employee);
    document.body.style.overflow = "hidden";
    if (mainContainer) {
      mainContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleClosePopup = () => {
    setSelectedAI(null);
    document.body.style.overflow = "unset";
  };

  const handleTabChange = (tab) => setActiveTab(tab);

  const getOwnedBotCodes = () => ownedSubscriptions.map((sub) => sub.bot.code);

  const filteredEmployees = aiEmployees.filter((emp) => {
    if (activeTab === "all") return true;
    return getOwnedBotCodes().includes(emp.code);
  });

  const getEmployeeOwnershipDetails = (employee) => {
    const subscription = ownedSubscriptions.find(
      (sub) => sub.bot.code === employee.code,
    );
    return { isOwned: !!subscription, subscription };
  };

  const selectedAIDetails = selectedAI
    ? getEmployeeOwnershipDetails(selectedAI)
    : { isOwned: false, subscription: null };
  const selectedAIIsActivated = selectedAI
    ? isAIActivated(selectedAI.name)
    : false;

  // Skeleton loader for cards
  const renderSkeletons = () => (
    <Grid container spacing="1.5rem">
      {[...Array(8)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Skeleton
            variant="rounded"
            height={200}
            sx={{ borderRadius: "1rem" }}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "1.5rem",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              animation: `${float} 3s ease-in-out infinite`,
            }}
          >
            <AutoAwesomeRounded sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              AI Managers
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your complete AI-powered legal team — 24/7 efficiency, 70% cost
              reduction.
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AnalyticsRounded
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              8 Specialized AIs
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityRounded
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              99.9% Accuracy
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RocketLaunchRounded
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              24/7 Available
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PowerSettingsNewRounded
              sx={{ color: theme.palette.success.main, fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              Active: {activatedAIs.length}/{aiEmployees.length}
            </Typography>
          </Box>
        </Stack>

        {/* Tabs */}
        <Box sx={{ display: "flex", gap: 1, mt: 3, flexWrap: "wrap" }}>
          <Button
            variant={activeTab === "all" ? "contained" : "outlined"}
            size="small"
            onClick={() => handleTabChange("all")}
            sx={{ borderRadius: "2rem", textTransform: "capitalize" }}
          >
            All Employees
          </Button>
          <Button
            variant={activeTab === "owned" ? "contained" : "outlined"}
            size="small"
            onClick={() => handleTabChange("owned")}
            sx={{ borderRadius: "2rem", textTransform: "capitalize" }}
          >
            Owned ({ownedSubscriptions.length})
          </Button>
          <Button
            variant={activeTab === "activated" ? "contained" : "outlined"}
            size="small"
            onClick={() => setActiveTab("activated")}
            sx={{
              borderRadius: "2rem",
              textTransform: "capitalize",
              bgcolor:
                activeTab === "activated"
                  ? theme.palette.success.main
                  : undefined,
              "&:hover": {
                bgcolor:
                  activeTab === "activated"
                    ? theme.palette.success.dark
                    : undefined,
              },
            }}
          >
            Activated ({activatedAIs.length})
          </Button>
        </Box>
      </Paper>

      {/* AI Employees Grid */}
      {loading || activationLoading ? (
        renderSkeletons()
      ) : (
        <Grid container spacing="1.5rem">
          {filteredEmployees.length > 0 ? (
            filteredEmployees
              .filter((emp) => {
                if (activeTab === "activated") {
                  return isAIActivated(emp.name);
                }
                return true;
              })
              .map((employee, index) => {
                const { isOwned, subscription } =
                  getEmployeeOwnershipDetails(employee);
                const isActivated = isAIActivated(employee.name);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={employee.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      whileHover={{ y: -6 }}
                      style={{ height: "100%" }}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          borderRadius: "1.25rem",
                          cursor: "pointer",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          ...(isActivated && {
                            borderColor: theme.palette.success.main,
                            boxShadow: `0 8px 20px ${alpha(theme.palette.success.main, 0.2)}`,
                            animation: `${pulse} 2s infinite`,
                          }),
                          "&:hover": {
                            boxShadow: `0 20px 40px ${alpha(
                              isActivated
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                              0.15,
                            )}`,
                            borderColor: isActivated
                              ? theme.palette.success.main
                              : alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                        onClick={(e) => handleCardClick(employee, e)}
                      >
                        <Box
                          sx={{
                            p: 2,
                            background: `linear-gradient(135deg, ${alpha(
                              isActivated
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                              0.05,
                            )} 0%, transparent 80%)`,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              background: isActivated
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              boxShadow: `0 6px 12px ${alpha(
                                isActivated
                                  ? theme.palette.success.main
                                  : theme.palette.primary.main,
                                0.2,
                              )}`,
                            }}
                          >
                            {React.cloneElement(employee.icon, {
                              sx: { fontSize: 26 },
                            })}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="h6" fontWeight={700} noWrap>
                              {employee.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {employee.title}
                            </Typography>
                          </Box>

                          {/* Activation Toggle - disabled if not owned */}
                          <Tooltip
                            title={
                              !isOwned
                                ? "Subscribe first to activate"
                                : isActivated
                                  ? "Deactivate"
                                  : "Activate"
                            }
                          >
                            <Box className="activation-switch">
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleActivationToggle(employee, e)
                                }
                                disabled={
                                  !isOwned ||
                                  activationInProgress === employee.name
                                }
                                sx={{
                                  color: isActivated
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary,
                                  "&:hover": {
                                    bgcolor: alpha(
                                      isActivated
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main,
                                      0.1,
                                    ),
                                  },
                                }}
                              >
                                {activationInProgress === employee.name ? (
                                  <CircularProgress size={20} />
                                ) : isActivated ? (
                                  <StopCircleOutlined />
                                ) : (
                                  <PlayCircleOutline />
                                )}
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </Box>

                        <Box sx={{ p: 2, pt: 0 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              height: 40,
                            }}
                          >
                            {employee.description}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-end",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              {isActivated && (
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleRounded />}
                                  sx={{ borderRadius: "1rem", fontWeight: 500 }}
                                />
                              )}
                              {isOwned ? (
                                <Chip
                                  label={`Subscribed • ${subscription?.status || "active"}`}
                                  size="small"
                                  color="info"
                                  icon={<CheckCircleRounded />}
                                  sx={{ borderRadius: "1rem", fontWeight: 500 }}
                                />
                              ) : employee.monthly_price_usd ? (
                                <Chip
                                  label={`$${employee.monthly_price_usd}/mo`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ borderRadius: "1rem", fontWeight: 600 }}
                                />
                              ) : (
                                <Chip
                                  label="Coming Soon"
                                  size="small"
                                  icon={<AccessTimeRounded />}
                                  sx={{
                                    borderRadius: "1rem",
                                    bgcolor: alpha(
                                      theme.palette.warning.main,
                                      0.1,
                                    ),
                                  }}
                                />
                              )}
                            </Box>
                            <ArrowForwardRounded
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            />
                          </Box>
                        </Box>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  borderRadius: "2rem",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {activeTab === "owned"
                    ? "No subscriptions yet"
                    : activeTab === "activated"
                      ? "No activated AIs"
                      : "No AI employees available"}
                </Typography>
                {(activeTab === "owned" || activeTab === "activated") && (
                  <Button
                    variant="contained"
                    onClick={() => setActiveTab("all")}
                    sx={{ mt: 2, borderRadius: "2rem" }}
                  >
                    Browse All
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* AI Details Popup */}
      <AnimatePresence>
        {selectedAI && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 9998,
              }}
              onClick={handleClosePopup}
            />
            <motion.div
              initial={{
                position: "fixed",
                left: cardPosition.x,
                top: cardPosition.y,
                width: cardPosition.width,
                height: cardPosition.height,
                zIndex: 9999,
                scale: 1,
                opacity: 1,
              }}
              animate={{
                left: "50%",
                top: "50%",
                x: "-50%",
                y: "-50%",
                width: "min(90vw, 800px)",
                height: "fit-content",
                scale: 1,
                opacity: 1,
              }}
              exit={{
                left: cardPosition.x,
                top: cardPosition.y,
                width: cardPosition.width,
                height: cardPosition.height,
                x: 0,
                y: 0,
                opacity: 0,
              }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              style={{ position: "fixed", zIndex: 9999 }}
            >
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "1.5rem",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <IconButton
                  onClick={handleClosePopup}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                    zIndex: 10,
                  }}
                >
                  <CloseRounded />
                </IconButton>

                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      selectedAIIsActivated
                        ? theme.palette.success.main
                        : theme.palette.primary.main,
                      0.05,
                    )} 0%, transparent 100%)`,
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          bgcolor: selectedAIIsActivated
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          mb: 2,
                          boxShadow: `0 12px 24px ${alpha(
                            selectedAIIsActivated
                              ? theme.palette.success.main
                              : theme.palette.primary.main,
                            0.3,
                          )}`,
                        }}
                      >
                        {React.cloneElement(selectedAI.icon, {
                          sx: { fontSize: 40 },
                        })}
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        color={
                          selectedAIIsActivated
                            ? "success.main"
                            : "primary.main"
                        }
                        gutterBottom
                      >
                        {selectedAI.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        gutterBottom
                      >
                        {selectedAI.title}
                      </Typography>

                      {/* Activation Status in Popup */}
                      <Chip
                        label={selectedAIIsActivated ? "Active" : "Inactive"}
                        color={selectedAIIsActivated ? "success" : "default"}
                        icon={
                          selectedAIIsActivated ? (
                            <CheckCircleRounded />
                          ) : (
                            <PowerSettingsNewRounded />
                          )
                        }
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Typography variant="h5" fontWeight={700} gutterBottom>
                        About {selectedAI.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        paragraph
                      >
                        {selectedAI.detailedDescription}
                      </Typography>

                      {selectedAIDetails.isOwned ? (
                        <Box>
                          <Alert
                            severity="success"
                            icon={<CheckCircleRounded />}
                            sx={{ borderRadius: "1rem", mb: 2 }}
                          >
                            You are subscribed to {selectedAI.name}. Status:{" "}
                            {selectedAIDetails.subscription?.status || "active"}
                          </Alert>

                          <Button
                            variant="contained"
                            size="medium"
                            startIcon={
                              activationInProgress === selectedAI.name ? null : selectedAIIsActivated ? (
                                <StopCircleOutlined />
                              ) : (
                                <RocketLaunchRounded />
                              )
                            }
                            onClick={(e) => handleActivationToggle(selectedAI, e)}
                            disabled={activationInProgress === selectedAI.name}
                            sx={{
                              borderRadius: "2rem",
                              px: 4,
                            }}
                          >
                            {activationInProgress === selectedAI.name ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : selectedAIIsActivated ? (
                              "Deactivate Bot"
                            ) : (
                              "Activate Bot"
                            )}
                          </Button>
                        </Box>
                      ) : selectedAI.monthly_price_usd ? (
                        <Box>
                          <Alert
                            severity="info"
                            icon={<AttachMoneyRounded />}
                            sx={{ borderRadius: "1rem", mb: 2 }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              Monthly: ${selectedAI.monthly_price_usd} | Annual:
                              ${selectedAI.annual_price_usd || "N/A"}
                              {selectedAI.setup_fee &&
                                ` | Setup: $${selectedAI.setup_fee}`}
                              {selectedAI.trial_days > 0 &&
                                ` | ${selectedAI.trial_days}-day trial`}
                            </Typography>
                          </Alert>

                          <Button
                            variant="contained"
                            size="medium"
                            startIcon={<RocketLaunchRounded />}
                            onClick={() => handleSubscribe(selectedAI.code)}
                            disabled={subscribing}
                            sx={{ borderRadius: "2rem", px: 4 }}
                          >
                            {subscribing ? (
                              <CircularProgress size={24} />
                            ) : (
                              "Subscribe Now"
                            )}
                          </Button>
                        </Box>
                      ) : (
                        <Alert
                          severity="warning"
                          icon={<AccessTimeRounded />}
                          sx={{ borderRadius: "1rem" }}
                        >
                          Coming Soon – In Development
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box
                  sx={{
                    p: 3,
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Key Features
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedAI.features.map((feature, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "1rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CheckCircleRounded
                            sx={{
                              fontSize: 20,
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Dialog
        open={foundingPopup.open}
        onClose={() => setFoundingPopup({ open: false, html: "" })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: "relative",
            borderRadius: "1.5rem",
            overflow: "hidden",
            background: theme.palette.background.paper,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.03,
              )} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
              "& .founding50": {
                padding: "2rem",
                textAlign: "center",
                fontFamily: theme.typography.fontFamily,
              },
              "& .f50-badge": {
                display: "inline-block",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: theme.palette.primary.main,
                background: alpha(theme.palette.primary.main, 0.1),
                padding: "0.25rem 0.75rem",
                borderRadius: "2rem",
                marginBottom: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              },
              "& .f50-title": {
                fontSize: "1.5rem",
                fontWeight: 700,
                color: theme.palette.text.primary,
                marginBottom: "0.75rem",
              },
              "& .f50-text": {
                fontSize: "1rem",
                color: theme.palette.text.secondary,
                marginBottom: "1.5rem",
                maxWidth: "400px",
                marginLeft: "auto",
                marginRight: "auto",
              },
              "& .f50-btn": {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: "#fff",
                border: "none",
                borderRadius: "2rem",
                padding: "0.75rem 2rem",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              },
            }}
          >
            {/* Backend HTML content */}
              <div
              dangerouslySetInnerHTML={{ __html: foundingPopup.html }}
              onClick={(e) => {
                if (e.target.closest(".f50-btn")) {
                  handleFoundingAccessRedirect();
                }
              }}
            />
          </Box>
        </DialogContent>

        <IconButton
          onClick={() => setFoundingPopup({ open: false, html: "" })}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: theme.palette.text.disabled,
            opacity: 0.3,
            transition: "opacity 0.2s",
            "&:hover": {
              opacity: 1,
              backgroundColor: alpha(theme.palette.text.disabled, 0.1),
            },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: "1rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIEmployeePage;
